import { ApiError, defaultErrorHandler } from 'api';
import orderBy from 'lodash/orderBy';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { DropdownItemProps } from 'semantic-ui-react';
import { AgeGroupsApi } from '../api';
import { AgeGroup } from '../types';

export interface AgeGroupLists {
  ageGroups: AgeGroup[];
  dropdownItems: DropdownItemProps[];
  indexedAgeGroups: Record<number, AgeGroup>;
  isFetched: boolean;
  isFetching: boolean;
}

export const useFetchAgeGroups = (options?: {
  staleTime?: number;
}): AgeGroupLists => {
  const {
    data: ageGroups,
    isFetching,
    isFetched,
  } = useQuery('ageGroups', AgeGroupsApi.getAll, {
    staleTime: 15 * 60 * 1000,
    onSettled: (_, error) =>
      error && error instanceof ApiError && defaultErrorHandler(error),
    ...options,
  });

  const indexedAgeGroups = useMemo(
    () =>
      (ageGroups || []).reduce((result, item) => {
        result[item.id] = item;
        return result;
      }, {} as Record<number, AgeGroup>),
    [ageGroups]
  );

  const dropdownItems = useMemo(() => {
    const activeAgeGroups = (ageGroups || []).filter(
      (ageGroup) => ageGroup.isActive
    );

    const sortedAgeGroups = orderBy(activeAgeGroups, (ageGroup) => {
      return Number(ageGroup.name.match(/\d+/)?.shift()) || 0;
    });

    const items = sortedAgeGroups.map((ageGroup) => ({
      key: ageGroup.id,
      value: ageGroup.id,
      text: ageGroup.name,
    }));

    return items;
  }, [ageGroups]);

  return {
    ageGroups: ageGroups || [],
    dropdownItems,
    indexedAgeGroups,
    isFetched,
    isFetching,
  };
};
